import { createSelector } from '@reduxjs/toolkit'
import _get from 'lodash/get'

import { makeTypedFetchSelectors } from 'app/redux-fetch/selectors'
import { RootState } from 'app/store'
import { DomainType, JobWithHomeType, PendingJobColumnType } from './types'

import {
  DOMAINS_CACHE_REDUCER_NAME,
  DOMAIN_TO_DOMAIN_DATA_CACHE_REDUCER_NAME,
  DOMAIN_TO_SUB_DOMAINS_CACHE_REDUCER_NAME,
  DOMAINS_REDUCER_NAME,
  SELECTED_NAV_DOMAIN_REDUCER_NAME,
  DOMAIN_JOBS_WITH_HOME_REDUCER_NAME,
  DOMAIN_JOBS_WITH_HOME_COUNT_REDUCER_NAME,
  DOMAIN_PENDING_JOBS_REDUCER_NAME,
  SELECTED_NAV_TOP_DOMAIN_REDUCER_NAME,
} from './consts'
import { DOMAIN_SUB_DOMAIN_FETCH_DATA } from 'components/DomainBreadcrumbs/consts'
import { currentRootDomainIdSelector } from 'features/rootDomains/selectors'
import { createHomeUrl, createJobUrl } from 'features/domains/utils'

export const selectedNavTopDomainSelector = (state: RootState) =>
  state[DOMAINS_REDUCER_NAME][SELECTED_NAV_TOP_DOMAIN_REDUCER_NAME]

export const selectedNavTopDomainIdSelector = createSelector(
  selectedNavTopDomainSelector,
  (selectedNavTopDomain) => selectedNavTopDomain?.id
)

export const selectedNavDomainSelector = makeTypedFetchSelectors<DomainType>(
  DOMAINS_REDUCER_NAME,
  SELECTED_NAV_DOMAIN_REDUCER_NAME
)

export const domainJobsWithHomeDataSelector = makeTypedFetchSelectors<
  JobWithHomeType[]
>(DOMAINS_REDUCER_NAME, DOMAIN_JOBS_WITH_HOME_REDUCER_NAME)

export const domainJobsWithHomeSelector = createSelector(
  [currentRootDomainIdSelector, domainJobsWithHomeDataSelector.data],
  (currentRootDomainId, domainJobsWithHomeData) =>
    domainJobsWithHomeData?.map((domainJobWithHome: JobWithHomeType) => {
      const rowAsLinkUrl = createHomeUrl(
        currentRootDomainId,
        domainJobWithHome?.homes?.[0]?.id
      )
      return { ...domainJobWithHome, rowAsLinkUrl }
    })
)

export const domainJobsWithHomeCountDataSelector = (state: RootState) =>
  state[DOMAINS_REDUCER_NAME][DOMAIN_JOBS_WITH_HOME_COUNT_REDUCER_NAME]

export const domainJobsWithHomeCountSelector = createSelector(
  domainJobsWithHomeCountDataSelector,
  (domainJobsWithHomeCountData) => domainJobsWithHomeCountData.data
)

export const domainPendingJobsDataSelector = makeTypedFetchSelectors<
  JobWithHomeType[]
>(DOMAINS_REDUCER_NAME, DOMAIN_PENDING_JOBS_REDUCER_NAME)

export const domainPendingJobsSelector = createSelector(
  [currentRootDomainIdSelector, domainPendingJobsDataSelector.data],
  (currentRootDomainId, domainPendingJobsData) =>
    domainPendingJobsData?.map((domainPendingJob: PendingJobColumnType) => {
      const rowAsLinkUrl = createJobUrl(
        currentRootDomainId,
        domainPendingJob?.id
      )
      return { ...domainPendingJob, rowAsLinkUrl }
    })
)

export const domainToSubDomainsCacheSelector = createSelector(
  (state: RootState) => state[DOMAINS_REDUCER_NAME],
  (domain) =>
    _get(domain, [
      DOMAINS_CACHE_REDUCER_NAME,
      DOMAIN_TO_SUB_DOMAINS_CACHE_REDUCER_NAME,
    ])
)

export const domainToDomainDataCacheSelector = createSelector(
  (state: RootState) => state[DOMAINS_REDUCER_NAME],
  (domain) =>
    _get(domain, [
      DOMAINS_CACHE_REDUCER_NAME,
      DOMAIN_TO_DOMAIN_DATA_CACHE_REDUCER_NAME,
    ])
)

export const domainSubDomainsFetchDataSelector =
  makeTypedFetchSelectors<DomainType>(
    DOMAINS_REDUCER_NAME,
    DOMAIN_SUB_DOMAIN_FETCH_DATA
  )
